import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Radio, ChevronRight, TrendingUp, AlertTriangle, BellRing, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const AUTH_HEADER = () => {
    const token = localStorage.getItem('valnexo_token') || localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
};

/**
 * Widget ADMIN — "Mes directives lancées" (Phase 3).
 * Affiché pour admin_full uniquement. Résume l'état LIVE des directives
 * actives récentes avec taux d'ack, d'action, dossiers traités, VP en retard.
 */
export default function MyDirectivesStatusWidget({ isAdminFull }) {
    const navigate = useNavigate();
    const [directives, setDirectives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [catchupBusy, setCatchupBusy] = useState(false);

    const handleCatchupPush = async () => {
        if (catchupBusy) return;
        const ok = window.confirm(
            "Renvoyer un push notification à tous les utilisateurs qui n'ont pas encore lu une directive ?\n\n" +
            "(Ignore la fenêtre anti-spam de 48h — à utiliser une seule fois pour rattrapage.)"
        );
        if (!ok) return;
        setCatchupBusy(true);
        try {
            const res = await axios.post(
                `${API}/api/directives/cron/relance-non-acked?min_hours_since_sent=0&min_hours_since_reminder=0`,
                {},
                { headers: AUTH_HEADER(), timeout: 60000 },
            );
            const n = res.data?.push_envoyes || 0;
            const d = res.data?.deliveries_traitees || 0;
            toast.success(`Rattrapage lancé : ${d} destinataire${d > 1 ? 's' : ''} · ${n} push envoyé${n > 1 ? 's' : ''}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impossible de lancer le rattrapage.");
        } finally {
            setCatchupBusy(false);
        }
    };

    useEffect(() => {
        if (!isAdminFull) {
            setLoading(false);
            return;
        }
        let mounted = true;
        const load = async () => {
            try {
                const res = await axios.get(`${API}/api/directives/mine-launched`, {
                    headers: AUTH_HEADER(),
                    timeout: 15000,
                });
                if (!mounted) return;
                setDirectives(res.data?.directives || []);
            } catch {
                // silencieux — widget se cache
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        const t = setInterval(load, 3 * 60 * 1000);
        return () => { mounted = false; clearInterval(t); };
    }, [isAdminFull]);

    if (!isAdminFull || loading || directives.length === 0) return null;

    return (
        <Card data-testid="admin-directives-status-widget" className="border-indigo-300 bg-gradient-to-br from-indigo-50/60 to-white">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <Radio className="w-5 h-5 text-indigo-700" />
                    <span>Mes directives — état live</span>
                    <Badge className="ml-1 bg-indigo-700 text-white">{directives.length}</Badge>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCatchupPush}
                        disabled={catchupBusy}
                        data-testid="admin-directives-catchup-push"
                        className="ml-auto h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                        title="Renvoyer un push aux non-lus (rattrapage one-shot)"
                    >
                        {catchupBusy ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <BellRing className="w-3.5 h-3.5" />
                        )}
                        <span className="ml-1 hidden sm:inline">Rattrapage push</span>
                    </Button>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5 pt-0">
                {directives.map((d) => {
                    const hasWarning = (d.vp_en_retard && d.vp_en_retard.length > 0) || (d.taux_ack_pct !== null && d.taux_ack_pct < 50);
                    const hasProgress = d.taux_dossiers_pct !== null && d.taux_dossiers_pct >= 70;
                    return (
                        <button
                            key={d.directive_id}
                            type="button"
                            onClick={() => navigate(`/dashboard?directive=${d.directive_id}`)}
                            data-testid={`admin-directive-item-${d.directive_id}`}
                            className="w-full text-left rounded-xl bg-white border border-indigo-200 p-3 hover:border-indigo-400 hover:shadow-sm transition-all"
                        >
                            <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-slate-900 text-sm truncate">{d.titre}</h4>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {d.destinataires_total} destinataire{d.destinataires_total > 1 ? 's' : ''}
                                        {d.dossiers_total > 0 && ` · ${d.dossiers_total} dossier${d.dossiers_total > 1 ? 's' : ''}`}
                                    </p>
                                </div>
                                {hasWarning && <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />}
                                {hasProgress && !hasWarning && <TrendingUp className="w-4 h-4 text-emerald-600 shrink-0" />}
                                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                            </div>

                            {/* Barres de progression */}
                            <div className="grid grid-cols-3 gap-3 mt-2.5">
                                <MiniStat label="Lus" pct={d.taux_ack_pct} color="purple" />
                                <MiniStat label="En action" pct={d.taux_action_pct} color="blue" />
                                {d.dossiers_total > 0 && (
                                    <MiniStat label="Dossiers traités" pct={d.taux_dossiers_pct} color="emerald" />
                                )}
                            </div>

                            {/* Alertes VP en retard */}
                            {d.vp_en_retard && d.vp_en_retard.length > 0 && (
                                <div className="mt-2 text-xs text-amber-800 bg-amber-50 rounded-md px-2 py-1.5">
                                    <strong>VP à la traîne :</strong>{' '}
                                    {d.vp_en_retard.map((v) => `${v.nom} (${v.dossiers_a_traiter}d)`).join(', ')}
                                </div>
                            )}
                        </button>
                    );
                })}
            </CardContent>
        </Card>
    );
}

function MiniStat({ label, pct, color }) {
    const val = pct == null ? '—' : `${pct}%`;
    const colorMap = {
        purple: 'bg-purple-500',
        blue: 'bg-blue-500',
        emerald: 'bg-emerald-500',
    };
    return (
        <div>
            <div className="flex items-baseline justify-between text-[10px] text-slate-500">
                <span>{label}</span>
                <span className="font-bold text-slate-800">{val}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                <div
                    className={`h-full ${colorMap[color]} transition-all duration-500`}
                    style={{ width: pct == null ? '0%' : `${pct}%` }}
                />
            </div>
        </div>
    );
}
